if (!customElements.get('newsletter-modal')) {
  customElements.define(
    'newsletter-modal',
    class NewsletterModal extends ModalElement {
      constructor() {
        super();
    
        // Prevent popup on Shopify robot challenge page
        if (window.location.pathname === '/challenge') {
          return;
        }
    
        if (!theme.config.isTouch || Shopify.designMode) {
          this.init();
        }
        else {
          new theme.initWhenVisible(theme.utils.throttle(this.init.bind(this)));
        }
      }

      connectedCallback() {
        super.connectedCallback();
        this.bindDismissHandlers();
      }

      bindDismissHandlers() {
        const overlay = this.overlay;
        if (overlay && !overlay.dataset.newsletterDismissBound) {
          overlay.dataset.newsletterDismissBound = 'true';
          overlay.addEventListener('click', () => {
            if (this.open) this.hide();
          });
        }
      }
    
      get shouldLock() {
        return true;
      }
    
      get testMode() {
        return this.getAttribute('data-test-mode') === 'true';
      }
    
      get delay() {
        return this.hasAttribute('data-delay') ? parseInt(this.getAttribute('data-delay')) : 5;
      }
    
      get expiry() {
        return this.hasAttribute('data-expiry') ? parseInt(this.getAttribute('data-expiry')) : 30;
      }
    
      get cookieName() {
        return 'concept:newsletter-popup';
      }

      get dismissCookieName() {
        return 'concept:newsletter-popup:dismissed';
      }

      get dismissCooldownKey() {
        return 'concept:newsletter-popup:dismissed-until';
      }

      /** Minimum time before the popup can show again after closing with X (seconds). */
      get dismissCooldownSeconds() {
        return 300;
      }

      get submited() {
        // Apex forms render success/error alerts in hidden containers by default.
        return this.querySelector(
          '[data-apex-form-success]:not([hidden]) .alert, [data-apex-form-alert]:not([hidden]) .alert'
        ) !== null;
      }

      readDismissUntil() {
        const storages = [localStorage, sessionStorage];

        for (const storage of storages) {
          try {
            const until = parseInt(storage.getItem(this.dismissCooldownKey), 10);
            if (!Number.isNaN(until) && until > Date.now()) return until;
          } catch (e) {}
        }

        return 0;
      }

      isDismissedRecently() {
        if (this.readDismissUntil() > Date.now()) return true;

        return this.getCookie(this.dismissCookieName) === 'true' || this.getCookie(this.cookieName) === 'true';
      }

      setDismissCooldown() {
        const until = Date.now() + this.dismissCooldownSeconds * 1000;
        const untilValue = String(until);

        try {
          localStorage.setItem(this.dismissCooldownKey, untilValue);
        } catch (e) {}

        try {
          sessionStorage.setItem(this.dismissCooldownKey, untilValue);
        } catch (e) {}

        if (theme.cookiesEnabled()) {
          this.setCookieMaxAge(this.dismissCookieName, this.dismissCooldownSeconds);
        }

        this.updateFloatingBarVisibility();
      }

      updateFloatingBarVisibility() {
        const bar = document.querySelector('.newsletter-bar');
        if (!bar) return;

        bar.hidden = this.isDismissedRecently();
      }
    
      init() {
        if (this.initialized) return;
        this.initialized = true;

        this.updateFloatingBarVisibility();
        
        // Open modal if errors or success message exist
        if (this.submited) {
          this.load(1);
          return;
        }

        if (this.shouldAutoOpen()) {
          this.load(this.delay);
        }
      }

      shouldAutoOpen() {
        if (this.isDismissedRecently()) return false;

        // Test mode only forces the popup in the theme editor preview.
        if (this.testMode) return Shopify.designMode;

        return !this.getCookie(this.cookieName);
      }
    
      load(delay) {
        if (Shopify.designMode) return;
    
        setTimeout(() => this.show(), delay * 1000);
      }
    
      beforeHide() {
        super.beforeHide();
        this.recordDismiss();
      }

      recordDismiss() {
        if (this._dismissRecorded) return;
        this._dismissRecorded = true;
        this.setDismissCooldown();

        if (theme.cookiesEnabled() && !(this.testMode && Shopify.designMode)) {
          this.setCookie(this.cookieName, this.expiry);
        }
      }

      afterShow() {
        super.afterShow();
        this._dismissRecorded = false;
        this.classList.add('show-image');
      }

      afterHide() {
        super.afterHide();
        this.classList.remove('show-image');
        this.recordDismiss();

        // Remove long-lived cookie in theme editor test mode only.
        if (this.testMode && Shopify.designMode) {
          this.removeCookie(this.cookieName);
          this.removeCookie(this.dismissCookieName);
        }
      }
    
      getCookie(name) {
        const match = document.cookie.match(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`);
        return match ? match[2] : null;
      }
    
      setCookie(name, expiry) {
        document.cookie = `${name}=true; max-age=${(expiry * 24 * 60 * 60)}; path=/`;
      }

      setCookieMaxAge(name, maxAgeSeconds) {
        document.cookie = `${name}=true; max-age=${maxAgeSeconds}; path=/`;
      }
    
      removeCookie(name) {
        document.cookie = `${name}=; max-age=0`;
      }
    }
  );
}
